// The account's Plan card: current plan, live usage meters, the pricing grid,
// and the upgrade / manage-billing actions. Every network call has a state, and
// it degrades safely: if entitlements can't load it falls back to the preview
// card, and while Stripe is dormant the buttons say so instead of erroring.

import { useEffect, useState } from "react";

import { ApiError, api, type Entitlements, type PlansResponse } from "../../lib/api";
import { friendlyError } from "../../lib/friendlyError";

function limitText(n: number, unlimited: number): string {
  return n === unlimited ? "Unlimited" : n.toLocaleString();
}

export default function PlanSection() {
  const [ent, setEnt] = useState<Entitlements | null>(null);
  const [plans, setPlans] = useState<PlansResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    Promise.all([api.getEntitlements(), api.getPlans()])
      .then(([e, p]) => { if (alive) { setEnt(e); setPlans(p); } })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, []);

  async function upgrade(planId: string) {
    setBusy(planId);
    setNote(null);
    try {
      const { url } = await api.startCheckout(planId);
      if (url) { window.location.href = url; return; }
      setNote("Checkout isn't open yet — we'll email you when billing goes live.");
    } catch (e) {
      setNote(e instanceof ApiError && e.status === 503
        ? "Billing isn't switched on yet — nothing to pay today."
        : friendlyError(e, "Couldn't start checkout. Try again in a moment."));
    } finally {
      setBusy(null);
    }
  }

  async function manage() {
    setBusy("__portal");
    setNote(null);
    try {
      const { url } = await api.openBillingPortal();
      window.location.href = url;
    } catch (e) {
      setNote(e instanceof ApiError && e.status === 400
        ? "No billing account yet — pick a plan first."
        : e instanceof ApiError && e.status === 503
          ? "Billing isn't switched on yet."
          : friendlyError(e, "Couldn't open the billing portal."));
    } finally {
      setBusy(null);
    }
  }

  // Entitlements unavailable → keep the page working with the preview card.
  if (failed) {
    return (
      <Card title="Plan" subtitle="What you're paying for and your usage caps.">
        <div className="row between" style={bannerStyle}>
          <div>
            <div style={{ fontWeight: 700 }}>Free during preview</div>
            <div className="muted" style={{ fontSize: 13 }}>Billing turns on when we exit preview.</div>
          </div>
          <span className="badge good">Active</span>
        </div>
      </Card>
    );
  }
  if (!ent || !plans) {
    return <Card title="Plan"><div className="muted" style={{ fontSize: 13 }}>Loading your plan…</div></Card>;
  }

  const unlimited = plans.unlimited;
  const promo = ent.plan === "free-promo";
  const currentPlan = plans.plans.find((p) => p.id === ent.plan);
  const currentName = currentPlan?.name ?? (promo ? "Free preview" : ent.plan);
  const reset = ent.periodEnd ? new Date(ent.periodEnd) : null;

  return (
    <Card title="Plan" subtitle="What you're on, your usage, and how to upgrade.">
      <div className="row between" style={bannerStyle}>
        <div>
          <div style={{ fontWeight: 700 }}>{promo ? "Free during preview" : currentName}</div>
          <div className="muted" style={{ fontSize: 13 }}>
            {promo
              ? "Unlimited while we're in preview. Billing turns on when we exit."
              : `Your ${currentName} plan.${reset ? ` Renews ${reset.toLocaleDateString()}.` : ""}`}
          </div>
        </div>
        <span className={`badge ${ent.status === "active" ? "good" : "warn"}`}>{ent.status}</span>
      </div>

      <div className="stack" style={{ gap: 12, marginTop: 2 }}>
        <Meter label="Sites" used={ent.sites.used} limit={ent.sites.limit} unlimited={unlimited} />
        <Meter label="Edits this period" used={ent.edits.used} limit={ent.edits.limit} unlimited={unlimited} />
        <Meter label="Redesigns this period" used={ent.rebuilds.used} limit={ent.rebuilds.limit} unlimited={unlimited} />
      </div>

      <div style={{ fontWeight: 600, marginTop: 6 }}>Plans</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
        {plans.plans.map((p) => {
          const current = p.id === ent.plan;
          return (
            <div
              key={p.id}
              className="stack"
              style={{
                gap: 6,
                padding: 14,
                borderRadius: 12,
                border: `1px solid ${current ? "var(--primary, #1c6b49)" : "var(--border)"}`,
                background: "var(--surface)",
              }}
            >
              <div className="row between">
                <strong>{p.name}</strong>
                {current && <span className="badge good">current</span>}
              </div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>
                ${p.priceUsd}
                <span className="muted" style={{ fontSize: 12, fontWeight: 500 }}>/mo</span>
              </div>
              <div className="muted" style={{ fontSize: 12 }}>
                {limitText(p.siteLimit, unlimited)} sites · {limitText(p.editLimit, unlimited)} edits · {limitText(p.rebuildLimit, unlimited)} redesigns
              </div>
              {!current && p.priceUsd > 0 && (
                <button
                  className="btn primary"
                  type="button"
                  disabled={busy === p.id}
                  style={{ alignSelf: "flex-start", marginTop: 2 }}
                  onClick={() => void upgrade(p.id)}
                >
                  {busy === p.id ? "Starting…" : "Upgrade"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="row" style={{ gap: 8 }}>
        <button className="btn" type="button" disabled={busy === "__portal"} onClick={() => void manage()}>
          Manage billing
        </button>
      </div>

      {note && <div className="badge warn" style={{ alignSelf: "flex-start" }}>{note}</div>}
      <div className="muted" style={{ fontSize: 12 }}>We'll email you before any charge. No card is stored here.</div>
    </Card>
  );
}

const bannerStyle: React.CSSProperties = {
  padding: 14,
  background: "#eef8fe",
  border: "1px solid #cceafa",
  borderRadius: 12,
};

function Meter({ label, used, limit, unlimited }: { label: string; used: number; limit: number; unlimited: number }) {
  const isUnlimited = limit === unlimited;
  const pct = isUnlimited ? 100 : limit <= 0 ? 100 : Math.min(100, Math.round((used / limit) * 100));
  const over = !isUnlimited && limit > 0 && used >= limit;
  return (
    <div>
      <div className="row between" style={{ marginBottom: 5 }}>
        <span style={{ fontSize: 13 }}>{label}</span>
        <span className="muted" style={{ fontSize: 12, fontVariantNumeric: "tabular-nums" }}>
          {used.toLocaleString()} / {isUnlimited ? "∞" : limit.toLocaleString()}
        </span>
      </div>
      <div style={{ height: 8, borderRadius: 999, background: "var(--surface-2, #eef0ee)", border: "1px solid var(--border)", overflow: "hidden" }}>
        <div
          style={{
            height: "100%",
            width: `${pct}%`,
            borderRadius: 999,
            background: over ? "#b4472f" : "var(--primary, #1c6b49)",
            opacity: isUnlimited ? 0.22 : 1,
          }}
        />
      </div>
    </div>
  );
}

function Card({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="card stack" style={{ gap: 12 }}>
      <div>
        <div style={{ fontSize: 16, fontWeight: 700 }}>{title}</div>
        {subtitle && <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}
